/**
 * QUEST panel — the job, as the player currently understands it.
 *
 * Deliberately shows only what they know: a hidden task is ABSENT until
 * observation reveals it (`visibleTasks`), and a task still gated behind an
 * unfinished prerequisite reads as locked rather than actionable. The panel is
 * therefore a readout of knowledge, not of the questline's internals.
 *
 * DOM only — no PixiJS (architecture rule 4 keeps the sim clean; this is the
 * other side of that line).
 */

import { unlocked, visibleTasks, type Questline, type QuestTask } from '../sim/quest';
import type { GameWindow } from './window';

const KIND_MARK: Record<QuestTask['kind'], string> = {
  eliminate: '†',
  steal: '$',
  sabotage: '⚡',
  observe: '◉',
  rescue: '☗',
  exfil: '⇱',
};

export class QuestLog {
  private readonly host: HTMLElement;
  private lastSig = '';

  constructor(win: GameWindow) {
    this.host = document.createElement('div');
    this.host.className = 'quest-list';
    win.body.appendChild(this.host);
  }

  /** `floorLabel` renders a floor index the way the rest of the UI does. */
  update(q: Questline | null, floorLabel: (floorIdx: number) => string): void {
    if (!q) {
      if (this.lastSig !== 'none') {
        this.lastSig = 'none';
        this.host.innerHTML = '<div class="quest-empty">No active contract.</div>';
      }
      return;
    }
    const tasks = visibleTasks(q);
    // Only touch the DOM when something actually changed — this runs per frame.
    const sig = tasks.map((t) => `${t.id}:${t.done}:${unlocked(q, t)}`).join('|');
    if (sig === this.lastSig) return;
    this.lastSig = sig;
    this.host.innerHTML = tasks
      .map((t) => {
        const state = t.done ? 'done' : unlocked(q, t) ? 'open' : 'locked';
        const mark = t.done ? '✓' : KIND_MARK[t.kind];
        const where = t.done ? '' : `<span class="quest-where">${floorLabel(t.floorIdx)}</span>`;
        return `<div class="quest-row quest-row--${state}">`
          + `<span class="quest-mark">${mark}</span>`
          + `<span class="quest-title">${escapeHtml(t.title)}</span>${where}</div>`;
      })
      .join('');
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}
